import {
  useCardContext,
  useExportCroppedSecondaryImage,
  useExportLiveView,
} from '@hts/entities/Card/libs';
import styles from '@hts/entities/Card/styles/InfoContent/Resend/ResendButton.module.scss';
import {
  getLogoForRequest,
  getMappedOrderTextFields,
  getSecondaryImageForRequest,
  setLogoFormDataForRequest,
  setSecondaryImageFormDataForRequest,
} from '@hts/entities/Card/utils';
import { useEditOrderMutation } from '@hts/shared/api';
import { IEditOrderDataOutput } from '@hts/shared/types';
import { HtsButton } from '@hts/shared/ui';
import * as Sentry from '@sentry/react';
import { memo, useCallback, useRef, useState } from 'react';
import { ConfirmPopup } from '../ConfirmPopup';
import { ThanksPopup } from '../ThanksPopup';

export const ResendButton = memo(() => {
  const confirmPopupRef = useRef(null);

  const card = useCardContext((state) => state.card);

  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showThanks, setShowThanks] = useState(false);

  const { renderExportLiveView, getStageBlob: getStageBlobForLiveView } = useExportLiveView({
    card,
  });

  const { renderExportCroppedSecondaryImage, getStageBlob: getStageBlobForCroppedSecondaryImage } =
    useExportCroppedSecondaryImage({
      card,
    });

  const [editOrderRequest] = useEditOrderMutation();

  const onResend = useCallback(async () => {
    if (!card || !card.orderId) {
      Sentry.captureMessage('onResend: Card or orderId is not defined', {
        level: 'error',
        tags: { screen: 'resend', phase: 'init' },
        extra: {
          cardExists: Boolean(card),
          orderId: card?.orderId,
        },
      });

      console.error('onResend: Card or orderId is not defined');

      return;
    }

    setLoading(true);

    const mainCardImageBlob = await getStageBlobForLiveView();

    if (!mainCardImageBlob) {
      Sentry.captureMessage('onResend: mainCardImageBlob is not defined', {
        level: 'warning',
        tags: { screen: 'resend', phase: 'generate-image' },
        extra: {
          cardId: card.cardId,
          orderId: card.orderId,
        },
      });

      console.error('onResend: mainCardImageBlob is not defined');

      setLoading(false);

      return;
    }

    const formData = new FormData();

    const data: IEditOrderDataOutput = {
      audioId: card.orderAudioId ? Number(card.audio_id) || card.orderAudioId : undefined,
      orderId: card.orderId,
      textFields: getMappedOrderTextFields(card.fields),
    };

    const secondaryImageState = card.secondaryImage;
    const logoState = card.logo;

    if (secondaryImageState) {
      data.secondaryImageData = getSecondaryImageForRequest(secondaryImageState);

      const isCorrect = await setSecondaryImageFormDataForRequest({
        secondaryImage: secondaryImageState,
        formData,
        getStageBlobForCroppedSecondaryImage,
      });

      if (!isCorrect) {
        Sentry.captureMessage('onResend: secondary image formData invalid', {
          level: 'warning',
          tags: { screen: 'resend', phase: 'secondary-image' },
          extra: { cardId: card.cardId, orderId: card.orderId, hasSecondaryImage: true },
        });

        setLoading(false);

        return;
      }
    }

    if (logoState) {
      data.logoData = getLogoForRequest(logoState);

      setLogoFormDataForRequest({ logo: logoState, formData });
    }

    formData.append('orderedCardFile', mainCardImageBlob);
    formData.append('data', JSON.stringify(data));

    try {
      const result = await editOrderRequest(formData);

      if ('data' in result) {
        sessionStorage.clear();
        setShowThanks(true);
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: { screen: 'resend', action: 'editOrderRequest' },
        extra: {
          cardId: card.cardId,
          orderId: card.orderId,
          hasLogo: !!card.logo,
          hasSecondary: !!card.secondaryImage,
        },
      });

      console.log('Error', err);
    }

    setLoading(false);
  }, [card, getStageBlobForLiveView, getStageBlobForCroppedSecondaryImage, editOrderRequest]);

  const onClickResendButton = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setShowConfirm(true);
  }, []);

  const onConfirmNext = useCallback(
    (e) => {
      e?.preventDefault();

      setShowConfirm(false);
      onResend();
    },
    [onResend]
  );

  const closeThanks = useCallback(() => {
    setShowThanks(false);
  }, []);

  const onCloseConfirm = useCallback(() => {
    setShowConfirm(false);
  }, []);

  return (
    <>
      {renderExportLiveView}
      {renderExportCroppedSecondaryImage}

      {showConfirm && (
        <ConfirmPopup ref={confirmPopupRef} nextPopup={onConfirmNext} closePopup={onCloseConfirm} />
      )}

      {showThanks && <ThanksPopup closePopup={closeThanks} />}

      <div className={styles.container}>
        <HtsButton
          label="Resend Card"
          title="Resend Card"
          typographyProps={{ fontWeight: '600' }}
          className={styles.resendButton}
          disabled={loading}
          onClick={onClickResendButton}
        />

        {loading && <div className="loader" />}
      </div>
    </>
  );
});
